<template>
    <div class="mb-8 space-y-2 max-w-7xl mx-auto">
        <div class="flex items-center justify-between bg-gray-50 py-2 px-3">
            <h4 class="text-lg text-center font-medium">{{ $t('Skills') }}</h4>
            <div class="flex justify-end">
                <button
                    class="bg-blue-500 text-white border border-blue-500 hover:border-blue-600 hover:bg-white hover:text-blue-800 transition duration-150 text-sm rounded px-3 py-2"
                    @click="open = true"
                >
                    {{ $t('Create New Skill') }}
                </button>
            </div>
        </div>

        <modal :closable="true" @close="closeModal" :title="$t('Create New Skill')" :open="open">
            <div slot="content">
                <span
                    class="my-12 flex items-center justify-center w-full border border-transparent bg-green-400 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                    v-if="submitted"
                >
                    <svg
                        class="w-5 h-5 text-white mr-1"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    {{ $t('Submitted') }}</span
                >
                <form v-else class="pt-6 pb-10 space-y-4" @submit.prevent="createSkill">
                    <div class="md:flex items-center space-y-2 md:space-y-0 md:space-x-3">
                        <section class="flex-1 space-y-1 flex flex-col">
                            <label for="tag" class="text-sm">{{ $t('Skill Name (JP)') }}</label>
                            <input
                                placeholder="マッサージ"
                                :disabled="submitting || submitted"
                                v-model="formTag"
                                name="tag"
                                class="form-input"
                            />
                        </section>
                        <section class="flex-1 space-y-1 flex flex-col">
                            <label for="tagEn" class="text-sm">{{
                                $t('Skill Name (English)')
                            }}</label>
                            <input
                                placeholder="Massage"
                                :disabled="submitting || submitted"
                                v-model="formTagEn"
                                name="tagEn"
                                class="form-input"
                            />
                        </section>
                    </div>
                    <section class="space-y-1 flex flex-col">
                        <span
                            v-if="formError"
                            class="bg-red-100 text-center text-sm py-1 px-1 rounded shake text-red-900 max-w-3xl overflow-auto"
                        >
                            {{ this.formError }}
                        </span>
                    </section>
                    <div>
                        <button
                            class="flex items-center justify-center w-full border border-transparent hover:bg-transparent hover:border-blue-500 hover:text-gray-900 transition duration-150 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                            :class="[
                                submitting || !this.formValid
                                    ? 'pointer-events-none bg-gray-200'
                                    : 'bg-blue-500',
                            ]"
                            type="submit"
                        >
                            <spinner v-if="submitting" class="text-white" />
                            <span v-else>{{ $t('Create') }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </modal>
        <ul v-for="(items, category, index) in skillGroups" :key="index">
            <h4 class="font-bold text-lg bg-indigo-50 rounded px-2 py-1 mb-1">{{ category }}</h4>

            <draggable
                class="list-group"
                tag="ul"
                v-model="skillGroups[category]"
                v-bind="dragOptions"
                @end="onReorder(category)"
            >
                <transition-group type="transition" :name="'flip-list'">
                    <li v-for="s in items" :key="s.id" class="group flex items-center mb-1">
                        <svg
                            class="h-4 w-4 text-gray-300 group-hover:text-indigo-500 cursor-pointer mr-2"
                            viewBox="0 0 96 96"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                        >
                            <path
                                d="M94.2422,43.7578l-12-12a5.9994,5.9994,0,0,0-8.4844,8.4844L75.5156,42H54V20.4844l1.7578,1.7578a5.9994,5.9994,0,0,0,8.4844-8.4844l-12-12a5.9979,5.9979,0,0,0-8.4844,0l-12,12a5.9994,5.9994,0,0,0,8.4844,8.4844L42,20.4844V42H20.4844l1.7578-1.7578a5.9994,5.9994,0,0,0-8.4844-8.4844l-12,12a5.9979,5.9979,0,0,0,0,8.4844l12,12a5.9994,5.9994,0,1,0,8.4844-8.4844L20.4844,54H42V75.5156l-1.7578-1.7578a5.9994,5.9994,0,0,0-8.4844,8.4844l12,12a5.9979,5.9979,0,0,0,8.4844,0l12-12a5.9994,5.9994,0,0,0-8.4844-8.4844L54,75.5156V54H75.5156l-1.7578,1.7578a5.9994,5.9994,0,1,0,8.4844,8.4844l12-12A5.9979,5.9979,0,0,0,94.2422,43.7578Z"
                            />
                        </svg>
                        <SkillCard :skill="s" class="flex-1" :categories="skillCategories" />
                    </li>
                </transition-group>
            </draggable>
        </ul>

        <spinner v-if="loading" />
    </div>
</template>
<script>
import api from '../lib/api'
import Draggable from 'vuedraggable'
import SkillCard from '../components/SkillCard.vue'

export default {
    components: {
        SkillCard,
        Draggable,
    },
    methods: {
        async onReorder(cat) {
            try {
                await api.updateSkillsSorting(this.skillGroups[cat])
            } catch (err) {
                console.log(err)
            }
        },
        async createSkill() {
            this.submitting = true
            this.formError = null
            const payload = {
                tag: this.formTag,
                tagEn: this.formTagEn,
            }
            await api.createSkill(payload)
            this.submitting = false
            this.submitted = true
        },
        closeModal() {
            this.formError = null
            this.open = false
            if (this.submitted) {
                this.$router.go()
            }
        },
        resetData() {
            this.formName = ''
        },
        findCatByKey(key) {
            let result = {
                id: 'others',
                tag: '他に',
            }
            this.skillCategories.forEach(s => {
                if (s.id === key) {
                    result = s
                }
            })

            return result
        },
    },
    computed: {
        formValid() {
            return (
                !!this.formTag && this.formTag !== '' && !!this.formTagEn && this.formTagEn !== ''
            )
        },
    },
    data() {
        return {
            dragOptions: {
                animation: 0,
                group: 'description',
                disabled: false,
                ghostClass: 'ghost',
            },
            skillGroups: {},
            skills: [],
            skillCategories: [],
            loading: false,
            formError: null,
            formTag: '',
            formTagEn: '',
            submitting: false,
            submitted: false,
            loadingMore: false,
            open: false,
        }
    },
    async mounted() {
        if (this.$route.query.reload === true) {
            this.$router.push('/skills')
        }
        //@Todo cache this in global state instead of fetching here??
        this.skillCategories = await api.getSkillCategories()
        this.skills = await api.getSkills()

        //Update skillGroup
        const byCat = {}
        this.skills.forEach(s => {
            /* console.log(s.tagEn, s.category) */
            const cat = this.findCatByKey(s.category)
            const key = cat.tag
            if (!byCat[key]) byCat[key] = []
            byCat[key].push(s)
        })
        this.skillGroups = byCat

        this.loading = false
    },
}
</script>
